"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileSpreadsheet, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  eyebrow: string;
  standards: string;
  title: string;
  lead: string;
  resourcesBtn: string;
  imgAlt: string;
  overlayBadge: string;
  footerSupply: string;
  footerAction: string;
  specSquareRangeLabel: string;
  specPlainDutchLabel: string;
  specTwillDutchLabel: string;
  specStandardsLabel: string;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    eyebrow: "Industrial Precision Wire Cloth",
    standards: "ISO 9044 / ASTM E2016",
    title: "Weave pattern determines aperture precision, mechanical strength, and filtration efficiency",
    lead: "MutualFilter engineers precision stainless steel wire cloth in Plain, Twilled, Plain Dutch, Twilled Dutch, Reverse Dutch, and Five-Heddle weave patterns using AISI 304, 316, and 316L for critical industrial solid-liquid separation, screening, and differential pressure operations.",
    resourcesBtn: "Explore Technical Resources",
    imgAlt: "High-precision automated industrial shuttleless wire cloth weaving loom",
    overlayBadge: "Weave Geometry Inspection",
    footerSupply: "Rolls, slit coils, cut panels & stamped discs",
    footerAction: "Selection Guide",
    specSquareRangeLabel: "Square Weave Range",
    specPlainDutchLabel: "Plain Dutch Weave",
    specTwillDutchLabel: "Twill Dutch Weave",
    specStandardsLabel: "Reference Standards"
  },
  zh: {
    eyebrow: "工业精密金属网制造",
    standards: "ISO 9044 / ASTM E2016",
    title: "网型决定网孔精度、机械强度与工业截留效率",
    lead: "MutualFilter 研发制造高精度不锈钢金属编织网，涵盖平纹、斜纹、平纹密纹、斜纹密纹、反向密纹及五综编织体系。选用优质 AISI 304、316 与 316L 不锈钢丝，满足从粗粒筛分到微米级严苛微滤的全方位工程需求。",
    resourcesBtn: "浏览技术资源库",
    imgAlt: "精密不锈钢工业金属丝网自动编织车间机组",
    overlayBadge: "微观织造结构检测",
    footerSupply: "原卷、分条卷带、定制切片与冲压滤圆片",
    footerAction: "选型指南",
    specSquareRangeLabel: "方孔网目数范围",
    specPlainDutchLabel: "平纹密纹网规格",
    specTwillDutchLabel: "斜纹密纹网规格",
    specStandardsLabel: "制造与检验标准"
  },
  es: {
    eyebrow: "Tela metálica de precisión industrial",
    standards: "ISO 9044 / ASTM E2016",
    title: "El patrón de tejido determina la precisión de apertura, la resistencia mecánica y la eficiencia de filtración",
    lead: "MutualFilter fabrica telas metálicas de precisión en tejidos liso, asargado, holandés liso, holandés asargado, holandés inverso y cinco lizos en AISI 304, 316 y 316L para separación sólido-líquido, cribado y alta presión diferencial.",
    resourcesBtn: "Explorar recursos técnicos",
    imgAlt: "Telar industrial automatizado para tela metálica de precisión",
    overlayBadge: "Inspección de geometría de tejido",
    footerSupply: "Rollos, bobinas cortadas, paneles a medida y discos",
    footerAction: "Guía de selección",
    specSquareRangeLabel: "Rango de tejido cuadrado",
    specPlainDutchLabel: "Tejido holandés liso",
    specTwillDutchLabel: "Tejido holandés asargado",
    specStandardsLabel: "Normas de referencia"
  },
  de: {
    eyebrow: "Industrielle Präzisions-Drahtgewebe",
    standards: "ISO 9044 / ASTM E2016",
    title: "Webmuster bestimmen Öffnungspräzision, mechanische Belastbarkeit und Filtrationseffizienz",
    lead: "MutualFilter fertigt Präzisions-Drahtgewebe in glatter Bindung, Köperbindung, glatter Tresse, Köpertresse, Panzertresse und Fünfschaftgewebe aus AISI 304, 316 und 316L für anspruchsvolle Fest-Flüssig-Trennung, Siebung und Differenzdruckprozesse.",
    resourcesBtn: "Technische Ressourcen ansehen",
    imgAlt: "Automatisierte industrielle Webmaschine für Präzisions-Drahtgewebe",
    overlayBadge: "Prüfung der Gewebegeometrie",
    footerSupply: "Rollen, Spaltbänder, Zuschnitte & gestanzte Ronden",
    footerAction: "Auswahlratgeber",
    specSquareRangeLabel: "Quadratmaschen-Bereich",
    specPlainDutchLabel: "Glatte Tresse",
    specTwillDutchLabel: "Köpertresse",
    specStandardsLabel: "Referenznormen"
  },
  fr: {
    eyebrow: "Toiles métalliques de précision industrielle",
    standards: "ISO 9044 / ASTM E2016",
    title: "Le mode de tissage détermine la précision de filtration, la résistance mécanique et l'efficacité",
    lead: "MutualFilter conçoit des toiles métalliques de précision en tissage uni, croisé, hollandais uni, hollandais croisé, hollandais inversé et cinq lisses en AISI 304, 316 et 316L pour la séparation solide-liquide, le criblage et les fortes pressions différentielles.",
    resourcesBtn: "Explorer les ressources techniques",
    imgAlt: "Métier à tisser industriel automatisé pour toile métallique de haute précision",
    overlayBadge: "Contrôle géométrique du tissage",
    footerSupply: "Rouleaux, bandes refendues, panneaux et disques découpés",
    footerAction: "Guide de sélection",
    specSquareRangeLabel: "Plage toiles carrées",
    specPlainDutchLabel: "Tissu hollandais uni",
    specTwillDutchLabel: "Tissu hollandais croisé",
    specStandardsLabel: "Normes de référence"
  },
  pt: {
    eyebrow: "Telas metálicas de precisão industrial",
    standards: "ISO 9044 / ASTM E2016",
    title: "O padrão de tecelagem define a precisão de abertura, a resistência mecânica e a eficiência de filtração",
    lead: "A MutualFilter projeta telas metálicas de precisão em ponto liso, sarjado, holandês liso, holandês sarjado, holandês reverso e cinco liços em AISI 304, 316 e 316L para separação sólido-líquido, peneiramento e alta pressão diferencial.",
    resourcesBtn: "Explorar recursos técnicos",
    imgAlt: "Tear industrial automático para fabricação de telas metálicas de precisão",
    overlayBadge: "Inspeção geométrica da tecelagem",
    footerSupply: "Rolos, fitas cortadas, painéis sob medida e discos estampados",
    footerAction: "Guia de seleção",
    specSquareRangeLabel: "Faixa de malha quadrada",
    specPlainDutchLabel: "Trama holandesa lisa",
    specTwillDutchLabel: "Trama holandesa sarjada",
    specStandardsLabel: "Normas de referência"
  }
};
export default function WeavePatternsHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  const technicalHighlights = [{
    label: copy.specSquareRangeLabel,
    val: "1 – 635 Mesh"
  }, {
    label: copy.specPlainDutchLabel,
    val: "8×85 – 80×400 Mesh"
  }, {
    label: copy.specTwillDutchLabel,
    val: "20×250 – 400×2800 Mesh"
  }, {
    label: copy.specStandardsLabel,
    val: "ISO 9044 / ASTM E2016"
  }];
  return <section id="weavepatternshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Technical Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 mf-caption font-mono tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.eyebrow}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-2.5 py-1 mf-caption font-mono text-foreground">
                <ShieldCheck className="size-3 text-primary inline" aria-hidden="true" />
                <span>{copy.standards}</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.title}
            </h1>

            {/* Supporting Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                  <FileSpreadsheet className="size-4 text-muted-foreground" aria-hidden="true" />
                  <span>{copy.resourcesBtn}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Technical Spec Display Card (5 Columns on lg) */}
          <Reveal delay={100} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media relative mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_mesh_weaving_loom.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_mesh_weaving_loom.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_mesh_weaving_loom.png-wvc-sizes" alt={copy.imgAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.overlayBadge}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.footerSupply}</span>
                  </div>
                  <Link to="/ProductSelectionPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.footerAction}</span>
                    <ArrowRight className="size-3" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}